import { useCallback, useEffect, useState } from "react";
import { isAdSensePage, loadAdSense } from "./adsense";
import type { CheckoutAttribution } from "./api";
import { loadMe, loadSiteConfig } from "./api";
import "./analytics.css";

type ConsentChoice = "necessary" | "analytics" | "marketing";
type ConsentRecord = { version: 1; choice: ConsentChoice; updatedAt: number };

const CONSENT_KEY = "jieyi_consent_v1";
const ATTRIBUTION_KEY = "jieyi_attribution_v1";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown> | IArguments>;
    gtag?: (...args: unknown[]) => void;
  }
}

function readConsent(): ConsentRecord | null {
  try {
    const value = JSON.parse(localStorage.getItem(CONSENT_KEY) ?? "null") as ConsentRecord | null;
    return value?.version === 1 && ["necessary", "analytics", "marketing"].includes(value.choice)
      ? value
      : null;
  } catch {
    return null;
  }
}

function consentFlags(choice: ConsentChoice) {
  return {
    analyticsConsent: choice === "analytics" || choice === "marketing",
    marketingConsent: choice === "marketing",
  };
}

function sendConsent(choice: ConsentChoice, command: "default" | "update") {
  window.dataLayer ??= [];
  window.gtag ??= function gtag(...args: unknown[]) {
    window.dataLayer?.push(args as unknown as IArguments);
  };
  const flags = consentFlags(choice);
  window.gtag("consent", command, {
    security_storage: "granted",
    functionality_storage: "granted",
    analytics_storage: flags.analyticsConsent ? "granted" : "denied",
    ad_storage: flags.marketingConsent ? "granted" : "denied",
    ad_user_data: flags.marketingConsent ? "granted" : "denied",
    ad_personalization: flags.marketingConsent ? "granted" : "denied",
  });
}

function loadGtm(containerId: string) {
  if (!/^GTM-[A-Z0-9]+$/.test(containerId) || document.querySelector("script[data-jieyi-gtm]"))
    return;
  window.dataLayer ??= [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.nonce = document.querySelector<HTMLScriptElement>("script[nonce]")?.nonce ?? "";
  script.dataset.jieyiGtm = "true";
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  document.head.appendChild(script);
}

function captureAttribution(choice: ConsentChoice) {
  const flags = consentFlags(choice);
  if (!flags.analyticsConsent) {
    try {
      localStorage.removeItem(ATTRIBUTION_KEY);
    } catch {
      /* 无存储权限时保持拒绝 */
    }
    return;
  }
  const query = new URLSearchParams(window.location.search);
  const current = {
    utmSource: query.get("utm_source") ?? undefined,
    utmMedium: query.get("utm_medium") ?? undefined,
    utmCampaign: query.get("utm_campaign") ?? undefined,
    utmContent: query.get("utm_content") ?? undefined,
    utmTerm: query.get("utm_term") ?? undefined,
    gclid: flags.marketingConsent ? (query.get("gclid") ?? undefined) : undefined,
    gbraid: flags.marketingConsent ? (query.get("gbraid") ?? undefined) : undefined,
    wbraid: flags.marketingConsent ? (query.get("wbraid") ?? undefined) : undefined,
    capturedAt: Date.now(),
  };
  try {
    const previous = JSON.parse(localStorage.getItem(ATTRIBUTION_KEY) ?? "{}");
    if (!flags.marketingConsent) {
      delete previous.gclid;
      delete previous.gbraid;
      delete previous.wbraid;
    }
    if (Object.values(current).some((value) => typeof value === "string" && value))
      localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(current));
    else localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(previous));
  } catch {
    /* 禁用存储时不保留归因 */
  }
}

async function enableConsentedAds(): Promise<void> {
  if (!isAdSensePage(new URL(window.location.href))) return;
  try {
    const me = await loadMe();
    // 广告只出现在未登录访问者的公开页面，不读取账户或付费取名结果。
    if (!me.user && readConsent()?.choice === "marketing") loadAdSense();
  } catch {
    /* 无法确认页面身份时不加载广告 */
  }
}

export function trackEvent(event: string, properties: Record<string, unknown> = {}) {
  const consent = readConsent();
  if (!consentFlags(consent?.choice ?? "necessary").analyticsConsent) return;
  window.dataLayer ??= [];
  window.dataLayer.push({ event, ...properties });
}

export function getCheckoutAttribution(): CheckoutAttribution {
  const consent = readConsent();
  const flags = consentFlags(consent?.choice ?? "necessary");
  let stored: Record<string, string | number | undefined> = {};
  try {
    stored = JSON.parse(localStorage.getItem(ATTRIBUTION_KEY) ?? "{}") as typeof stored;
  } catch {
    stored = {};
  }
  const gaCookie = document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith("_ga="))
    ?.split("=")
    .slice(1)
    .join("=");
  const clientId = gaCookie?.split(".").slice(-2).join(".");
  return {
    analyticsConsent: flags.analyticsConsent,
    marketingConsent: flags.marketingConsent,
    analyticsClientId: flags.analyticsConsent && clientId ? clientId : undefined,
    analyticsSessionId: undefined,
    utmSource: flags.analyticsConsent ? String(stored.utmSource ?? "") || undefined : undefined,
    utmMedium: flags.analyticsConsent ? String(stored.utmMedium ?? "") || undefined : undefined,
    utmCampaign: flags.analyticsConsent ? String(stored.utmCampaign ?? "") || undefined : undefined,
    utmContent: flags.analyticsConsent ? String(stored.utmContent ?? "") || undefined : undefined,
    utmTerm: flags.analyticsConsent ? String(stored.utmTerm ?? "") || undefined : undefined,
    gclid: flags.marketingConsent ? String(stored.gclid ?? "") || undefined : undefined,
    gbraid: flags.marketingConsent ? String(stored.gbraid ?? "") || undefined : undefined,
    wbraid: flags.marketingConsent ? String(stored.wbraid ?? "") || undefined : undefined,
  };
}

export function openConsentSettings() {
  window.dispatchEvent(new Event("jieyi:consent-settings"));
}

export function AnalyticsConsent({ language }: { language: "zh" | "en" }) {
  const [containerId, setContainerId] = useState("");
  const [open, setOpen] = useState(false);
  const zh = language === "zh";

  useEffect(() => {
    void loadSiteConfig()
      .then((config) => {
        const id = config.features.analytics.gtmContainerId;
        setContainerId(id);
        const consent = readConsent();
        sendConsent(consent?.choice ?? "necessary", "default");
        if (consent) {
          if (consent.choice !== "necessary") loadGtm(id);
          captureAttribution(consent.choice);
          if (consent.choice === "marketing") void enableConsentedAds();
        } else if (id || isAdSensePage(new URL(window.location.href))) {
          setOpen(true);
        }
      })
      .catch(() => {
        /* 配置不可用时保持默认拒绝 */
      });
  }, []);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener("jieyi:consent-settings", show);
    return () => window.removeEventListener("jieyi:consent-settings", show);
  }, []);

  const choose = useCallback(
    (choice: ConsentChoice) => {
      try {
        localStorage.setItem(
          CONSENT_KEY,
          JSON.stringify({ version: 1, choice, updatedAt: Date.now() } satisfies ConsentRecord),
        );
      } catch {
        setOpen(false);
        return;
      }
      sendConsent(choice, "update");
      captureAttribution(choice);
      if (choice !== "necessary") loadGtm(containerId);
      setOpen(false);
      if (choice === "marketing") void enableConsentedAds();
      else if (document.querySelector("script[data-jieyi-adsense]")) window.location.reload();
    },
    [containerId],
  );

  if (!open) return null;
  return (
    <section aria-label={zh ? "隐私设置" : "Privacy settings"} className="consent-panel">
      <div>
        <strong>{zh ? "你的隐私，由你决定" : "Your privacy, your choice"}</strong>
        <p>
          {zh
            ? "必要存储用于登录和安全。允许广告后，Google AdSense 可在公开页面展示广告并使用 Cookie。账户与付费取名结果页不加载 AdSense；你可随时修改选择。"
            : "Essential storage supports sign-in and security. With advertising consent, Google AdSense may show ads and use cookies on public pages. Account and paid naming results do not load AdSense. You can change your choice at any time."}
        </p>
        <a href="/cookies">{zh ? "查看 Cookie 政策" : "Read the Cookie Policy"}</a>
      </div>
      <div className="consent-actions">
        <button type="button" onClick={() => choose("necessary")}>
          {zh ? "仅必要" : "Essential only"}
        </button>
        <button type="button" onClick={() => choose("analytics")}>
          {zh ? "允许分析" : "Allow analytics"}
        </button>
        <button className="primary" type="button" onClick={() => choose("marketing")}>
          {zh ? "全部允许" : "Allow all"}
        </button>
      </div>
    </section>
  );
}
